// Worker pool for the gen + mesh streaming pipeline (perf). Through M31 the
// streamer ran ONE gen worker and ONE mesh worker, so generation and meshing
// were each pinned to a single core — fine at walking pace, but an aircraft
// crosses ~7 chunks/second (AVI-2) and outruns a single mesher. This pool fans
// jobs across `hardwareConcurrency` workers, dispatching each to the least-loaded
// one so a slow job never starves the rest.
//
// PURE plumbing over a minimal `WorkerLike`: it constructs no `Worker` itself
// (the DOM factory lives beside each caller), so it unit-tests with fake workers.

/** The slice of the DOM Worker API the pool needs (real `Worker` satisfies it;
 *  tests inject fakes). */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  onmessage: ((ev: MessageEvent) => void) | null;
  terminate(): void;
}

/** Index of the least-loaded worker (ties resolve to the lowest index). */
export function leastLoaded(pending: readonly number[]): number {
  let best = 0;
  for (let i = 1; i < pending.length; i++) if (pending[i]! < pending[best]!) best = i;
  return best;
}

/** Desired pool size from a core-count hint (`navigator.hardwareConcurrency`),
 *  leaving one core for the main thread and clamping to [1, max]. Non-finite or
 *  unknown hints assume a modest 4 cores. */
export function poolSize(hint: number | undefined, max: number): number {
  const cores = typeof hint === 'number' && Number.isFinite(hint) && hint > 0 ? Math.floor(hint) : 4;
  return Math.max(1, Math.min(max, cores - 1));
}

/** A fixed set of workers behind a single result callback, with least-loaded
 *  dispatch. Jobs are fungible for balancing; the message payload carries whatever
 *  the caller needs to route a result (chunk coords, jobId, rev). */
export class WorkerPool<TResult = unknown> {
  private readonly pending: number[];

  constructor(
    private readonly workers: readonly WorkerLike[],
    onResult: (data: TResult) => void,
  ) {
    this.pending = workers.map(() => 0);
    workers.forEach((w, i) => {
      w.onmessage = (ev): void => {
        if (this.pending[i]! > 0) this.pending[i]!--;
        onResult(ev.data as TResult);
      };
    });
  }

  get size(): number { return this.workers.length; }
  /** Total jobs dispatched but not yet returned (across the whole pool). */
  get inflight(): number { let n = 0; for (const p of this.pending) n += p; return n; }

  /** Dispatch one job to the least-loaded worker (optionally transferring buffers). */
  post(message: unknown, transfer?: Transferable[]): void {
    const i = leastLoaded(this.pending);
    this.pending[i]!++;
    this.workers[i]!.postMessage(message, transfer);
  }

  /** Send a message to every worker (init/config — never transferred, so the same
   *  buffers can be cloned into each worker). */
  broadcast(message: unknown): void {
    for (const w of this.workers) w.postMessage(message);
  }

  terminate(): void {
    for (const w of this.workers) w.terminate();
    this.pending.fill(0);
  }
}
